import { FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Circle, Dialog, Portal, Text } from '@chakra-ui/react'
import { PaymentIcon } from '@editor/assets/icons'
import { MODAL_TYPES } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { ModalProps } from '@editor/interfaces/interfaces'
import { DialogCloser } from '@infraloop/common'

/**
 * Provides the editor's `GlobalModalContext` — the confirm/error dialog the editor body
 * relies on (error surfacing, the user-not-found logout flow, destructive-action confirms).
 * It lives in `app/editor`'s own shell too; the editor package doesn't own it because it is
 * app chrome. Hatsuly hosts the same editor body (slice #127), so it mounts an equivalent
 * provider around the editor routes.
 */
const DEFAULT_MODAL_PROPS: ModalProps = {
  body: '',
  cancelText: 'キャンセル',
  resolveText: '',
  closable: true,
  confirmText: '確定',
  modalType: MODAL_TYPES.CONFIRMATION,
  onConfirm: () => true,
  onCancel: () => null,
  onResolve: () => null,
  isConfirmable: true,
  title: '注意',
  size: 'sm',
}
export const ERROR_MODAL_PROPS: ModalProps = {
  ...DEFAULT_MODAL_PROPS,
  confirmText: '了解',
  modalType: MODAL_TYPES.ERROR,
  title: 'エラー',
}

const GlobalModal: FC<PropsWithChildren> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [props, setProps] = useState<ModalProps>(DEFAULT_MODAL_PROPS)

  const showModal = useCallback((modalProps: ModalProps) => {
    setIsOpen(true)
    setProps({
      ...(modalProps.modalType === MODAL_TYPES.ERROR ? ERROR_MODAL_PROPS : DEFAULT_MODAL_PROPS),
      ...modalProps,
    })
  }, [])

  const showErrorModal = useCallback((message: string) => {
    setIsOpen(true)
    setProps({
      ...ERROR_MODAL_PROPS,
      body: message,
    })
  }, [])

  const updateModal = useCallback(
    (modalProps: ModalProps) => {
      setProps({
        ...props,
        ...modalProps,
      })
    },
    [props],
  )

  const hideModal = useCallback(() => {
    setIsOpen(false)
  }, [])

  const contextValue = useMemo(
    () => ({ showModal, showErrorModal, updateModal, hideModal }),
    [showModal, showErrorModal, updateModal, hideModal],
  )

  const handleModalToggle = () => {
    hideModal()
  }

  const handleModalConfirm = async () => {
    if (props.onConfirmPromised) {
      setIsLoading(true)
      if (await props.onConfirmPromised()) {
        hideModal()
      }
      setIsLoading(false)
    } else if (props.onConfirm && props.onConfirm()) {
      hideModal()
    }
  }

  const handleModalCancel = () => {
    if (props.onCancel) {
      props.onCancel()
    }
    hideModal()
  }

  const handleModalResolve = () => {
    if (props.onResolve) {
      props.onResolve()
    }
    hideModal()
  }

  const getColorScheme = (modalType: string | undefined) => {
    switch (modalType) {
      case MODAL_TYPES.CONFIRMATION_CRITICAL:
        return 'red'
      default:
        return 'blue'
    }
  }

  /**
   * If this is a loader modal, run the loader function and close the modal when it's done.
   */
  useEffect(
    () => {
      if (isOpen) {
        void (async () => {
          if (props.loader) {
            const result = await props.loader()
            if (result && props.closeOnLoaded) {
              hideModal()
            }
          }
        })()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  )

  const renderComponent = () => {
    if (!isOpen) {
      return null
    }

    return (
      <Dialog.Root
        closeOnEscape={props.closable}
        closeOnInteractOutside={props.closable}
        id="globalModal"
        open
        onOpenChange={(e) => {
          if (!e.open) handleModalToggle()
        }}
        size={props.size}
        placement="center"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                <Text fontSize="xl" fontWeight="semibold" color="gray2.700">
                  {props.title}
                </Text>
                {props.closable && <DialogCloser />}
              </Dialog.Header>
              <Dialog.Body gap={4} alignItems="start" w="100%" color="gray2.600">
                {props.leftIcon ? (
                  <Circle bg="orange.100" color="orange.600" size="2rem" position="relative" top="1rem" left="1.5rem">
                    <PaymentIcon size="1.2rem" />
                  </Circle>
                ) : null}

                <Text whiteSpace="pre-wrap">{props.body}</Text>
              </Dialog.Body>
              <Dialog.Footer>
                {props.modalType !== MODAL_TYPES.LOADING && (
                  <>
                    {props.modalType !== MODAL_TYPES.ALERT &&
                      props.modalType !== MODAL_TYPES.ERROR &&
                      props.closable && (
                        <Button variant="ghost" onClick={handleModalCancel}>
                          {props.cancelText}
                        </Button>
                      )}
                    {props.resolveText && props.onResolve && (
                      <Button onClick={handleModalResolve}>{props.resolveText}</Button>
                    )}
                    <Button
                      colorPalette={getColorScheme(props.modalType)}
                      onClick={handleModalConfirm}
                      disabled={!props.isConfirmable || isLoading}
                      loading={isLoading}
                    >
                      {props.confirmText}
                    </Button>
                  </>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    )
  }

  return (
    <GlobalModalContext.Provider value={contextValue}>
      {renderComponent()}
      {children}
    </GlobalModalContext.Provider>
  )
}

export default GlobalModal
